import {flow, getEnv, getSnapshot, getType, types} from "mobx-state-tree";
import {GroupFilterSelectState} from "../../elements/filters/GroupFilterSelectState";
import {AdvancedConfFilterSelectState} from "../../elements/filters/AdvancedConfFilterSelectState";
import lodash from "lodash";
import {DimensionSelectViewState} from "../../elements/dimensions/dimensionSelectViewState";
import {TagFilterSelectState} from "../../elements/filters/TagFilterSelectState";
import {filterSessionConstants, formConstants} from "../../../utils/constants";
import {ModalState} from "../../elements/modal/GenericModal";
import {ExtendedDataListModel} from "../../../models/extendedData/ExtendedDataListModel";
import {ExtendedDataSetListModel} from "../../../models/extendedData/ExtendedDataSetListModel";
import {
  applySearchParamsFromFilters,
  clearAllFilters,
  loadFiltersFromSession,
  persistFiltersToSession
} from "../../../utils/filterStorage";
import {TableColumn} from "../../tables/ScanbuyTable";

const ExtendedDataListViewState = types.model("ExtendedDataListViewState", {
  modalStore: types.maybe(ModalState),
  setStore: types.maybe(types.late(() => ExtendedDataSetListModel)),
  store: types.maybe(types.late(() => ExtendedDataListModel)),
  activeTab: types.optional(types.string, '1'),
  isFilterVisible: types.optional(types.boolean, true),
  isFilterTypeOR: types.optional(types.boolean, true),
  advFilterSelectState: types.maybeNull(types.reference(AdvancedConfFilterSelectState)),
  tagFilterSelectState: types.maybeNull(types.reference(TagFilterSelectState)),
  dimensionSelectViewState: types.maybeNull(types.reference(DimensionSelectViewState)),
  groupFilterSelectState: types.maybeNull(types.reference(GroupFilterSelectState)),
  filterGroup: types.optional(types.boolean, true),
  searchTxt : types.optional(types.string,''),
  isInitialLoad: types.optional(types.boolean, true),
  optionalColumns: types.optional(types.array(types.string), []),
  columnNameMapping: types.optional(types.map(types.string), {}),
  columns: types.maybeNull(types.array(types.late(() => TableColumn))),
  hasLoadedFiltersFromSession: types.optional(types.boolean, false),
  tableOptionalColumns: types.optional(types.array(types.string), [])
})
  .volatile((self) => ({
    modalComponent: types.object,
    modalComponentStore: types.object,
  }))
  .actions((self) => ({
    getSetStore() {
      return self.setStore;
    },
    handleRowClick(row, componentStore, component) {
      self.modalComponent = component;
      self.modalComponentStore = componentStore;
      self.modalStore.handleTitle(componentStore.title);
      self.modalStore.toggle();
    },
    getStore() {
      return self.store;
    },
    setActiveTab(tab) {
      self.activeTab = tab;
      self.refresh();
      self.getFilters();
    },
    getCurrentStore() {
      return self.activeTab === "1" ? self.getSetStore() : self.getStore();
    },
    setOnSelectChanges() {
      if (self.isInitialLoad) {
        self.isInitialLoad = false; // Set to false after first load
        return;
      }

      self.advFilterSelectState?.setConsumer(self.selectFilter)
      self.dimensionSelectViewState?.setFilterConsumer(self.selectFilter);
      self.tagFilterSelectState?.setConsumer(self.selectFilter)
      self.groupFilterSelectState?.setConsumer(self.selectFilter)
    },
    toggleFilterVisible() {
      self.isFilterVisible = !self.isFilterVisible
      self.saveFiltersToSessionStorage()
    },
    setTableOptionalColumns(columns) {
      self.tableOptionalColumns = columns;
    },
    setColumnNameMapping(mapping) {
      self.columnNameMapping = mapping
    },
    toggleColumnVisibility(id) {
      if (self.optionalColumns.includes(id)) {
        self.optionalColumns.remove(id);
      } else {
        self.optionalColumns.push(id);
      }
      self.saveColumnsToSession();
    },
    setColumns(col) {
      self.columns = col
    },
    saveColumnsToSession() {
      try {
        const columns = getSnapshot(self.optionalColumns);
        sessionStorage.setItem("extendedDataListColumns", JSON.stringify(columns));
      } catch (e) {
        console.warn("Failed to save column visibility to sessionStorage", e);
      }
    },
    loadColumnsFromSession() {
      try {
        const saved = sessionStorage.getItem("extendedDataListColumns");
        if (saved) {
          self.optionalColumns = JSON.parse(saved);
        }
      } catch (e) {
        console.warn("Failed to load column settings from sessionStorage", e);
      }
    },
    selectFilter(obj, covered){
      self.saveFiltersToSessionStorage()
      self.getCurrentStore().removeSearchParams(covered,/^dimension/);
      if(obj){
        const transpiledDimensions = {};
        if (!lodash.isEmpty(obj.dimensions)) {
          obj.dimensions.forEach(dimension => {
            const [uuid, value] = dimension.split('_');
            if (transpiledDimensions.hasOwnProperty(`dimension[${uuid}]`)) {
              transpiledDimensions[`dimension[${uuid}]`] += ',' + value;
            } else {
              transpiledDimensions[`dimension[${uuid}]`] = value;
            }
          });
          delete obj.dimensions
        }

        const params = lodash.mapValues(obj,(value, key) => {
          return {}[key] = value.join(',');
        });
        self.getCurrentStore().addSearchParams({...params,...transpiledDimensions})
        self.refresh()
        const prevGroupUuid = self.prevGroupUuid || [];
        const currentGroupUuid = obj.groupUuid || [];
        if (!lodash.isEqual(prevGroupUuid, currentGroupUuid)) {
          self.getFilters();
          self.filterGroup = false
          self.prevGroupUuid = currentGroupUuid;
        }
      }
    },
    onSearchChange(txt){
      console.log(txt?.currentTarget?.value);
      const searchText = txt?.currentTarget?.value;
      self.searchTxt = searchText
      if (searchText) {
        if (searchText.length > 2) {
          self.getCurrentStore().removeSearchParams(["q"]);
          self.getCurrentStore().addSearchParams({ q: searchText })
          self.refresh()
        } else {
          self.getCurrentStore().removeSearchParams(["q"])
        }
      } else {
        self.getCurrentStore().removeSearchParams(["q"]);
        self.refresh()
      }
      self.saveFiltersToSessionStorage()
    },
    isItemUnused: flow(function* (id) {
      self.isLoading = true;
      if (!id) {
        console.warn(`${getType(self).name} Warn: ID is 'undefined' or 'null'`);
      }

      let response;
      try {
        // fetch all codes that has extendedData item with id
        response = yield getEnv(self).codeManager.fetchAllCodes({
          extendedDataIds: id
        }, self);
      } catch (e) {
        console.error(`${getType(self).name} Error: Failed to assign isItemUnused`, e);
      }

      self.isLoading = false;

      return response && response.results?.length === 0;
    }),
    selectFilterMethod() {
      if (!self.isFilterTypeOR) {
        self.isFilterTypeOR = !self.isFilterTypeOR
        self.getCurrentStore().addSearchParams({tagFilterType: formConstants.Mode.OR})
      } else {
        self.isFilterTypeOR = !self.isFilterTypeOR
        self.getCurrentStore().addSearchParams({tagFilterType: formConstants.Mode.AND})
      }
      self.refresh()
      self.saveFiltersToSessionStorage()
    },
    getFilters() {
      self.getCurrentStore().fetchAllFilters();
    },
    refresh() {
      self.getCurrentStore().fetchAll();
    },
    saveFiltersToSessionStorage() {
      persistFiltersToSession(self, filterSessionConstants.CONFIG_SESSION_STORAGE_KEY);
    },
    loadFiltersFromSessionStorage() {
      if (self.hasLoadedFiltersFromSession) {
        return;
      }
      loadFiltersFromSession(self, filterSessionConstants.CONFIG_SESSION_STORAGE_KEY);
      applySearchParamsFromFilters(self);
      self.isElasticSearchEnabled ? self.search() : self.refresh();
      self.hasLoadedFiltersFromSession = true;
    },
    clearFilters() {
      clearAllFilters(self, filterSessionConstants.CONFIG_SESSION_STORAGE_KEY);
      self.hasLoadedFiltersFromSession = false;
    },
    addSearchParamsFromLoadedFilters() {
      applySearchParamsFromFilters(self);
    }
  }));

export default ExtendedDataListViewState;
